import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin, Phone, MessageCircle, CheckCircle, Clock, ShieldCheck, Wrench, Star, ArrowRight } from "lucide-react";
import { services, getBusinessConfig } from "@/lib/site-config";
import { CTABlock } from "@/components/CTABlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { MOTBookingForm } from "@/components/MOTBookingForm";
import { TrustSection } from "@/components/TrustSection";
import { AreaContactCTA } from "@/components/AreaContactCTA";
import { ArrivalFAQ } from "@/components/ArrivalFAQ";
import { DirectionsWidget } from "@/components/DirectionsWidget";
import { trackEvent } from "@/lib/tracking";
import { getPublicConfig } from "@/lib/site-content.functions";
import { useEffect } from "react";
import { seoHead } from "@/lib/seo";
import { PrimaryCTA } from "@/components/PrimaryCTA";

interface LocalContent {
  slug: string;
  name: string;
  postcode: string;
  hero: string;
  intro: string;
  whyHere: string[];
  highlights: { title: string; desc: string }[];
  faqs: { q: string; a: string }[];
  nearbyRoads: string;
  travelTime: string;
  /** Bespoke trust signals shown on this area page only — local-business proof
   * that's specific to this neighbourhood. Optional so older pages don't break. */
  localTrust?: { label: string; detail: string }[];
}

export const localAreas: LocalContent[] = [
  {
    slug: "poplar",
    name: "Poplar",
    postcode: "E14",
    hero: "MOT, Servicing & Repairs in Poplar — From £35",
    intro:
      "Looking for a trusted garage in Poplar? Woodseer Autos is just minutes from Poplar High Street and Poplar DLR. As a fully DVSA-authorised MOT testing station (V106376), we keep local drivers on the road with honest advice, fair pricing and quick turnaround where parts and workshop load allow.",
    whyHere: [
      "Walk-in MOTs welcome — most tests done within the hour",
      "5-minute walk from Poplar DLR — drop the car off and take the train",
      "Easy access from East India Dock Road, Aspen Way and the A12",
      "Trusted by Poplar residents and tradespeople for years",
      "All makes and models — daily commuters, family cars, vans and premium motors",
      "12-month parts and labour warranty on all repair work",
    ],
    highlights: [
      { title: "MOT Testing in Poplar", desc: "Class 4 MOTs typically completed within around an hour. Honest pass/fail with clear advisories. Free retest if repairs done with us." },
      { title: "Servicing Near Poplar High Street", desc: "Interim, full and major services for all makes. Genuine and OE-quality parts, manufacturer-stamp friendly." },
      { title: "Fast Diagnostics", desc: "Warning light on? We use professional diagnostic equipment to find the real fault — no guesswork." },
      { title: "Brakes, Clutch & Suspension", desc: "Mechanical repairs done right first time. Quotes given upfront, with no work without your sign-off." },
    ],
    faqs: [
      { q: "How close are you to Poplar DLR?", a: "We're roughly a 5-minute walk from Poplar DLR — perfect if you want to drop your car off in the morning and take the train into work." },
      { q: "Do I need to book my MOT in Poplar?", a: "We recommend calling ahead so we can give you a slot, but we do welcome walk-ins for MOTs and quick checks." },
      { q: "Do you work on vans for Poplar tradespeople?", a: "Yes — we service and repair light commercial vehicles. Many local tradespeople in E14 trust us with their vans." },
      { q: "Can you collect my car from Poplar?", a: "Call us — depending on your location and our diary that day, we can sometimes arrange collection from the surrounding area." },
      { q: "How much does an MOT cost in Poplar?", a: "Call us for our current MOT pricing. We're competitively priced with no hidden fees, and any required repairs are quoted before any work starts." },
    ],
    localTrust: [
      { label: "DVSA station number", detail: "V106376 — verifiable on the official MOT history checker for any Poplar resident before booking." },
      { label: "Workshop type", detail: "Independent, family-run — no national-chain upsells, no commission-driven advisors." },
      { label: "Years in E14", detail: "Established workshop on Pixley Street with a long-standing local customer base from Poplar High Street, Chrisp Street and All Saints estates." },
      { label: "Repair guarantee", detail: "Every repair carries a 12-month parts-and-labour warranty, honoured in-house." },
      { label: "Quote policy", detail: "No work starts without a written quote you've signed off — itemised parts, labour and VAT." },
    ],
    nearbyRoads: "East India Dock Road, Poplar High Street, Aspen Way and the A12",
    travelTime: "5 min walk from Poplar DLR · 8 min drive from Canary Wharf",
  },
  {
    slug: "canary-wharf",
    name: "Canary Wharf",
    postcode: "E14",
    hero: "Trusted Garage Near Canary Wharf — MOT, Service, Repair",
    intro:
      "Working in Canary Wharf? Drop your car off before work and we'll have it ready by the end of the day. Woodseer Autos is a short drive from the estate, with full MOT testing, servicing and diagnostics under one roof. Perfect for professionals who want zero disruption to their working day.",
    whyHere: [
      "10-minute drive from the Wharf — drop off before work, collect after",
      "Quick access via Aspen Way and the Limehouse Link",
      "Premium and executive vehicles welcome — Mercedes, BMW, Audi, Porsche, Range Rover",
      "Clear text or call updates so you know exactly when your car is ready",
      "DVSA-authorised MOT station for cars and light vans (Classes 1, 2, 3, 4, 5, 7)",
      "12-month workmanship warranty on every repair",
    ],
    highlights: [
      { title: "MOT for Canary Wharf Workers", desc: "Drop off before 9am, collect after work. We'll text or call you when it's ready and walk you through any advisories." },
      { title: "Premium Servicing", desc: "Dealer-quality service without dealer prices. Service book stamped, OE-quality parts as standard." },
      { title: "Executive Car Repairs", desc: "Experienced with Mercedes-AMG, Porsche, Audi RS, Bentley and Range Rover. Honest assessment, fair quotes." },
      { title: "Diagnostics That Save You Money", desc: "Warning lights diagnosed with professional equipment — not guesswork. We fix the actual fault." },
    ],
    faqs: [
      { q: "Can I drop my car off before work in Canary Wharf?", a: "Yes — many of our Canary Wharf customers drop off first thing and collect after work. Just call ahead so we can have a slot ready." },
      { q: "Do you work on premium and executive cars?", a: "Yes. We regularly service and repair Mercedes-AMG, BMW M, Audi RS, Porsche, Bentley and Range Rover. Quality work without dealer-level bills." },
      { q: "How do I get to Woodseer from Canary Wharf?", a: "It's a short drive via Aspen Way or the Limehouse Link. We're at 16 Pixley Street, E14 7DF — entrance via 14 Copenhagen Place." },
      { q: "How much is an MOT for a Canary Wharf driver?", a: "Call us for our latest MOT pricing. We're competitive and there are no hidden fees — any required repairs are quoted before work starts." },
      { q: "Do you offer a courtesy car?", a: "Call us to discuss — depending on the job and timing, we may be able to help with alternative arrangements." },
    ],
    localTrust: [
      { label: "Premium-marque experience", detail: "Routine work on Mercedes-AMG, Porsche 911/Cayenne, Audi RS, Range Rover and BMW M cars driven by Wharf-based professionals." },
      { label: "Drop-off discipline", detail: "Pre-9am drop-off slots ring-fenced for Canary Wharf commuters so the car is back on the road before close-of-business." },
      { label: "Parts policy", detail: "OE or OE-equivalent only on premium vehicles — never the cheapest pattern part. Brand and price shown on the quote." },
      { label: "Communication", detail: "WhatsApp / text updates with photos of any advisory before we touch tools — designed for people who can't take calls in meetings." },
      { label: "Confidentiality", detail: "No advertising of customer plates, no social-media of vehicles. Discreet by default for high-profile drivers." },
    ],
    nearbyRoads: "Aspen Way, Westferry Road, the Limehouse Link tunnel and the A1261",
    travelTime: "8 min drive from Canary Wharf · 10 min via Aspen Way",
  },
  {
    slug: "isle-of-dogs",
    name: "Isle of Dogs",
    postcode: "E14",
    hero: "Local Garage on the Isle of Dogs — MOT & Servicing, E14",
    intro:
      "Living on the Isle of Dogs means you need a garage you can rely on without long drives across town. Woodseer Autos is just minutes from Millwall, Mudchute and South Quay — DVSA-authorised, all services under one roof, and a reputation built on honest workmanship.",
    whyHere: [
      "Local — short drive from Millwall, Mudchute and South Quay",
      "Easy access via Manchester Road and Westferry Road",
      "Full range of services — MOT, servicing, diagnostics, repairs, bodywork",
      "MOT and service often bookable together — call to check today's availability",
      "Trusted by Isle of Dogs residents and small business owners",
      "Honest assessment and clear quotes before any work starts",
    ],
    highlights: [
      { title: "MOT on the Isle of Dogs", desc: "DVSA-authorised testing station — Class 4 MOTs typically completed within around an hour. Clear pass/fail with plain-English advisories." },
      { title: "Full & Interim Servicing", desc: "Keep your manufacturer warranty intact with our independent service — service book stamped, OE-quality parts." },
      { title: "Brakes, Suspension & More", desc: "Squealing brakes? Knocking suspension? We diagnose and fix mechanical faults the right way, first time." },
      { title: "Bodywork & Paint", desc: "Car-park dents, scrapes and bumper damage repaired and colour-matched on-site." },
    ],
    faqs: [
      { q: "Is Woodseer Autos easy to get to from the Isle of Dogs?", a: "Very easy — a quick drive via Manchester Road or Westferry Road. We're at 16 Pixley Street, E14 7DF. You can also reach us by DLR." },
      { q: "Can you do an MOT and service on the same day?", a: "In most cases yes. Call ahead and we'll schedule both together so you only make one trip." },
      { q: "Do you handle older vehicles as well as new?", a: "Yes — we work on all makes, models and ages, from older runabouts to brand-new cars." },
      { q: "Do you handle bodywork repairs locally?", a: "Yes — dents, scratches, bumper repairs and full panel work, all done in-house with professional colour matching." },
      { q: "Do you provide a warranty on repairs?", a: "Yes — every repair we do is covered by a 12-month parts and labour warranty." },
    ],
    localTrust: [
      { label: "Island-friendly logistics", detail: "We can usually fit Isle of Dogs drivers in the same week — the bridges and one-way routes mean people often delay servicing, and we plan around that." },
      { label: "All-makes capability", detail: "Older runarounds and family hatchbacks treated with the same care as the newer fleet cars from the Wharf — no minimum job value." },
      { label: "Bodywork in-house", detail: "Car-park scrapes from Asda Crossharbour, Mudchute or Marsh Wall colour-matched and resprayed on-site, not subcontracted." },
      { label: "Genuine independence", detail: "No targets, no upsell scripts. Recommendations are based on the test sheet, not a bonus." },
      { label: "Transparent advisories", detail: "Every advisory is photographed and emailed so you can decide what to fix now versus next service." },
    ],
    nearbyRoads: "Manchester Road, Westferry Road, Marsh Wall and East Ferry Road",
    travelTime: "10 min drive from Mudchute · 12 min from South Quay",
  },
  {
    slug: "limehouse",
    name: "Limehouse",
    postcode: "E14",
    hero: "MOT & Car Repairs in Limehouse — Honest, Fast, Local",
    intro:
      "Limehouse drivers have easy access to Woodseer Autos via Commercial Road and the surrounding streets. Whether you're coming from Narrow Street, Salmon Lane or further along the A13, we're a straightforward drive — and we offer the full set of garage services under one roof.",
    whyHere: [
      "Quick access from Commercial Road, Narrow Street and the A13",
      "Walk to Limehouse DLR or station while we work on your car",
      "Trusted by Limehouse residents — strong reputation for honest pricing",
      "DVSA-authorised MOT testing station (V106376)",
      "Full diagnostics for warning lights and electrical faults",
      "12-month parts and labour warranty on all repair work",
    ],
    highlights: [
      { title: "MOT in Limehouse", desc: "Class 4 MOTs typically completed within the hour. Clear results, no hidden charges, free retest after repairs." },
      { title: "Servicing Near Commercial Road", desc: "Interim, full and major services for all makes — service book stamped, manufacturer-warranty safe." },
      { title: "Diagnostics for Warning Lights", desc: "Engine management, ABS or airbag light on? We diagnose and explain — no scare tactics, no upselling." },
      { title: "Mechanical Repairs Done Right", desc: "From brake pads to clutch replacements, our experienced mechanics quote fairly and fix it first time." },
    ],
    faqs: [
      { q: "How close are you to Limehouse station?", a: "We're a short drive or about a 10-minute walk from both Limehouse DLR and the mainline station — perfect if you want to drop your car off and take the train." },
      { q: "Is parking available when I drop off?", a: "Yes — you can park at the garage when dropping your vehicle off. We'll take it from there." },
      { q: "Can you sort warning-light diagnostics in Limehouse?", a: "Yes, we have professional-grade diagnostic equipment that covers all major manufacturers and can investigate any warning light or fault code." },
      { q: "Do you do bodywork in Limehouse?", a: "Yes — dent removal, scratch repair, bumper work and full panel resprays with colour matching, all done in-house." },
      { q: "Will I be told before any extra work is done?", a: "Always. We never carry out work without your approval first. You'll get a clear explanation and quote before we start." },
    ],
    localTrust: [
      { label: "Walk-to-station drop-off", detail: "Drop the car, walk 10 minutes to Limehouse DLR or the c2c mainline — half our Limehouse customers do this and collect after work." },
      { label: "Diagnostic kit", detail: "Multi-marque OE-level diagnostic equipment covering all major manufacturers — not a generic OBD reader from eBay." },
      { label: "Honest fault-finding", detail: "If we can't find the fault first time, the diagnostic time is on us — we don't bill for guesswork." },
      { label: "Recall & service-history aware", detail: "We check the DVSA database and any open recalls on your reg before we start, so nothing's missed." },
      { label: "Clear pricing", detail: "MOT from £35, no hidden fees, no booking fees, no card surcharges." },
    ],
    nearbyRoads: "Commercial Road, Narrow Street, the A13 and Burdett Road",
    travelTime: "5 min drive from Limehouse · 10 min walk from the DLR",
  },
  {
    slug: "docklands",
    name: "Docklands",
    postcode: "E14",
    hero: "Docklands Garage — MOT, Servicing & Repairs in E14",
    intro:
      "Whether you live in Wapping, work near West India Quay or commute through the Royal Docks, Woodseer Autos is the local independent garage Docklands drivers turn to. DVSA-authorised, full service capability under one roof, and honest pricing — no dealer mark-up, no surprise bills.",
    whyHere: [
      "Easy access from the Limehouse Link, A1203 and A13",
      "5-minute drive from West India Quay, 10 from the Royal Docks",
      "DVSA-authorised MOT testing station (V106376) — Classes 1, 2, 3, 4, 5 & 7",
      "Trusted by Docklands residents, dock workers and commuters",
      "MOT and service often bookable together — one trip where possible",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT Testing for Docklands Drivers", desc: "Class 4 MOTs typically completed within around an hour. Clear pass/fail explained in plain English. Free retest after repairs done with us." },
      { title: "Servicing for Daily Commutes", desc: "Interim, full and major services for all makes — service book stamped, OE-quality parts, manufacturer warranty intact." },
      { title: "Diagnostics & Electrics", desc: "Engine management, ABS, infotainment faults — we use professional diagnostic equipment to pinpoint the actual cause." },
      { title: "Brakes, Suspension & Bodywork", desc: "Mechanical repairs and panel work done in-house. Quotes given upfront, with no work without your sign-off." },
    ],
    faqs: [
      { q: "How do I get to Woodseer from the Docklands?", a: "We're a short drive via the Limehouse Link or East India Dock Road. Sat-nav to '14 Copenhagen Place, E14 7DF' and you'll arrive directly at the yard entrance." },
      { q: "Can I get to you by DLR?", a: "Yes — Westferry, Limehouse and Poplar DLR are all walking distance. Drop the car off and take the train into work or back home." },
      { q: "Do you handle vans for Docklands tradespeople?", a: "Yes — we work on light commercial vehicles up to Class 7 and many local tradespeople trust us with their vans." },
      { q: "Are you open Saturdays?", a: "Yes — we're open Monday to Saturday 9am–6pm. Saturdays book up fast, so call ahead if you need a specific slot." },
      { q: "Will I get a written quote before any work starts?", a: "Always. We never carry out work without your approval first. You'll get a clear explanation and a written estimate before we proceed." },
    ],
    localTrust: [
      { label: "Class 7 van capability", detail: "Up to 3,500kg — Transits, Sprinters, Vivaros and Crafters all tested and serviced on-site for Docklands tradespeople." },
      { label: "Fleet-friendly", detail: "Multi-vehicle and small-fleet servicing for local trades and couriers — invoiced monthly, with PDF service records on request." },
      { label: "Saturday opening", detail: "Open until 6pm on Saturdays — rare in E14 and built around people whose weekday is taken." },
      { label: "Workshop CCTV", detail: "Yard and bay CCTV gives you and us a clear record of the vehicle's condition on arrival and departure." },
      { label: "Parts traceability", detail: "Every part fitted is recorded against your reg with batch / supplier reference, in case of a future warranty claim." },
    ],
    nearbyRoads: "the Limehouse Link, A1203, East India Dock Road and the A13",
    travelTime: "5 min drive from West India Quay · 10 min from the Royal Docks",
  },
  {
    slug: "mile-end",
    name: "Mile End",
    postcode: "E1",
    hero: "Mile End Garage — MOT, Servicing & Repairs from £35",
    intro:
      "Living or working around Mile End? Woodseer Autos is a short hop down Burdett Road — a DVSA-authorised MOT station with full servicing, diagnostics and repairs under one roof. Drop the car off, walk to Mile End tube and we'll have it back on the road the same day.",
    whyHere: [
      "5-minute drive from Mile End tube via Burdett Road",
      "Easy access from the A11, Grove Road and Mile End Road",
      "DVSA-authorised MOT station (V106376) — Classes 1, 2, 3, 4, 5 & 7",
      "All makes — daily drivers, family cars, vans and premium motors",
      "MOT and service often bookable together — call to check today's availability",
      "12-month parts and labour warranty on all repair work",
    ],
    highlights: [
      { title: "MOT Testing in Mile End", desc: "Class 4 MOTs typically completed within around an hour. Honest pass/fail with plain-English advisories. Free retest after repairs done with us." },
      { title: "Servicing for Mile End Drivers", desc: "Interim, full and major services for every make. Service book stamped, OE-quality parts, manufacturer warranty intact." },
      { title: "Diagnostics Done Properly", desc: "Engine management or ABS light on? Professional diagnostic kit pinpoints the actual fault — no scare tactics." },
      { title: "Brakes, Clutch, Suspension", desc: "Mechanical repairs done right first time. Quotes given upfront, no work without your sign-off." },
    ],
    faqs: [
      { q: "How far is Woodseer from Mile End tube?", a: "About a 5-minute drive down Burdett Road, or a 20-minute walk. Many customers drop off and hop on the Central line." },
      { q: "Do you do walk-in MOTs from Mile End?", a: "We recommend calling ahead so we can guarantee a slot, but walk-ins are welcome when the diary allows." },
      { q: "Can you sort warning-light faults?", a: "Yes — we have professional diagnostic equipment that covers all major manufacturers. We'll diagnose and explain in plain English." },
      { q: "Will I get a written quote first?", a: "Always. We never carry out work without your approval. You'll get a clear estimate before any spanner is lifted." },
      { q: "Are you open Saturdays?", a: "Yes — Monday to Saturday 9am–6pm. Saturdays book up fast so call ahead." },
    ],
    nearbyRoads: "Burdett Road, Grove Road, Mile End Road and the A11",
    travelTime: "5 min drive from Mile End tube · 8 min from Stepney Green",
  },
  {
    slug: "bow",
    name: "Bow",
    postcode: "E3",
    hero: "Bow Garage — MOT, Service & Repair, Honest Pricing",
    intro:
      "Bow drivers know the value of a local garage they can trust. Woodseer Autos is a short drive down Bow Common Lane — DVSA-authorised, full service capability, and a reputation built on honest workmanship and fair pricing.",
    whyHere: [
      "Short drive from Bow Road, Bow Church DLR and Devons Road DLR",
      "Easy access from the A11, A12 and Bow Common Lane",
      "DVSA-authorised MOT station — Classes 1, 2, 3, 4, 5 & 7",
      "Trusted by Bow residents and small-business owners",
      "MOT and service often bookable together — call to check today's availability",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT in Bow E3", desc: "Class 4 MOTs typically done within the hour. Clear pass/fail and free retest after repairs." },
      { title: "Servicing for Daily Drivers", desc: "Interim, full and major services — book stamped, OE-quality parts, warranty-safe." },
      { title: "Engine Diagnostics", desc: "Professional fault-code reading and live-data testing for every major manufacturer." },
      { title: "Bodywork & Paint", desc: "Car-park dents, scrapes and bumper damage repaired and colour-matched on-site." },
    ],
    faqs: [
      { q: "How easy is it to get to you from Bow?", a: "Very easy — a 10-minute drive via Bow Common Lane or the A12. Sat-nav to '14 Copenhagen Place, E14 7DF'." },
      { q: "Can I leave the car and take the DLR?", a: "Yes — Devons Road and Bow Church DLR are both nearby, and we'll text you when the car is ready." },
      { q: "Do you work on vans?", a: "Yes — light commercial vehicles up to Class 7. Many local tradespeople trust us with their vans." },
      { q: "Will I be told before any extra work?", a: "Always. No work happens without your written approval first." },
      { q: "How much does an MOT cost?", a: "Call us for current pricing — we're competitive with no hidden fees." },
    ],
    nearbyRoads: "Bow Road, Bow Common Lane, the A11 and the A12",
    travelTime: "10 min drive from Bow Church · 8 min from Devons Road DLR",
  },
  {
    slug: "bethnal-green",
    name: "Bethnal Green",
    postcode: "E2",
    hero: "Bethnal Green Garage — MOT, Servicing, Diagnostics",
    intro:
      "Bethnal Green has plenty of garages — but few combine DVSA-authorised MOT testing, full diagnostics and honest pricing under one roof. Woodseer Autos is a short drive down Cambridge Heath Road and the A11 — the local choice for drivers who want straight answers.",
    whyHere: [
      "10-minute drive via Cambridge Heath Road and the A11",
      "Drop off and hop back on Central line or Overground",
      "DVSA-authorised MOT station (V106376)",
      "Honest assessment — no upselling, no scare tactics",
      "All makes from daily drivers to premium and executive",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT for Bethnal Green Drivers", desc: "Class 4 MOTs typically completed within around an hour. Clear pass/fail with plain-English advisories." },
      { title: "Full & Interim Servicing", desc: "Manufacturer-warranty safe. Service book stamped, OE-quality parts as standard." },
      { title: "Diagnostics & Electrics", desc: "Engine, ABS, airbag or infotainment faults — diagnosed properly and explained clearly." },
      { title: "Mechanical Repairs", desc: "Brakes, clutch, suspension and timing belts. Done right first time, with a written quote upfront." },
    ],
    faqs: [
      { q: "How long does an MOT take?", a: "Most Class 4 MOTs are completed within around an hour. We'll text or call you with the result." },
      { q: "Can you collect the car from Bethnal Green?", a: "Call us — depending on our diary that day, we can sometimes arrange collection." },
      { q: "Do you handle hybrid and electric vehicles?", a: "Yes — we work on hybrid powertrains and EVs. Call ahead so we can confirm any specifics." },
      { q: "Are you open Saturdays?", a: "Yes — Monday to Saturday 9am–6pm." },
      { q: "Will my warranty stay valid?", a: "Yes — independent servicing using OE-quality parts and a stamped service book keeps your manufacturer warranty intact." },
    ],
    nearbyRoads: "Cambridge Heath Road, Bethnal Green Road, Roman Road and the A11",
    travelTime: "10 min drive from Bethnal Green tube · 12 min from Cambridge Heath",
  },
  {
    slug: "whitechapel",
    name: "Whitechapel",
    postcode: "E1",
    hero: "Whitechapel Garage — MOT, Service & Repair, E1",
    intro:
      "Whitechapel sits right on the doorstep — Woodseer Autos is a quick drive down Commercial Road or via the A13. DVSA-authorised, all services in-house, and known across E1 for fair pricing and honest mechanics. Drop off, jump on the Elizabeth line, collect after work.",
    whyHere: [
      "Short drive via Commercial Road or the A13",
      "Drop off and use Whitechapel Elizabeth line / District / H&C / Overground",
      "DVSA-authorised MOT station — Classes 1, 2, 3, 4, 5 & 7",
      "All makes welcome — small runabouts to premium motors",
      "MOT and service often bookable together — call to check today's availability",
      "12-month workmanship warranty on every repair",
    ],
    highlights: [
      { title: "MOT in Whitechapel E1", desc: "Class 4 MOTs typically completed within around an hour. Free retest if repairs are done with us." },
      { title: "Servicing for E1 Drivers", desc: "Interim, full and major services for all makes — service book stamped." },
      { title: "Diagnostics That Save Money", desc: "Warning lights diagnosed properly — we fix the actual fault, not the symptom." },
      { title: "Brakes, Suspension & Bodywork", desc: "Mechanical and panel work in-house, with quotes given upfront." },
    ],
    faqs: [
      { q: "Can I drop off and take the Elizabeth line?", a: "Yes — Whitechapel station is one stop from Canary Wharf and three from Liverpool Street. Drop off, work, collect." },
      { q: "Do you take walk-in MOTs from Whitechapel?", a: "We recommend booking but walk-ins are welcome when the diary allows. Call first to be sure." },
      { q: "Do you handle premium and executive cars?", a: "Yes — Mercedes, BMW, Audi, Porsche, Range Rover, Bentley. Dealer-quality work without dealer prices." },
      { q: "How quickly can I get a slot?", a: "Often same-day or next-day. Call us with what you need." },
      { q: "Will I get a quote before any work?", a: "Always. No work without your written approval." },
    ],
    nearbyRoads: "Commercial Road, Whitechapel Road, the A13 and Cambridge Heath Road",
    travelTime: "10 min drive from Whitechapel · 8 min from Aldgate East",
  },
  {
    slug: "wapping",
    name: "Wapping",
    postcode: "E1W",
    hero: "Wapping Garage — MOT & Servicing, Quick & Honest",
    intro:
      "Wapping residents tell us they want one thing — a garage that doesn't muck them about. Woodseer Autos is a short drive via The Highway and the Limehouse Link. DVSA-authorised MOT, full servicing, fair pricing and a 12-month repair warranty — every time.",
    whyHere: [
      "10-minute drive via The Highway and the Limehouse Link",
      "Drop off and walk to Wapping Overground or Tower Hill",
      "DVSA-authorised MOT testing station (V106376)",
      "All makes — including older classics and premium motors",
      "MOT and service often bookable together — call to check today's availability",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT for Wapping Drivers", desc: "Class 4 MOTs done within the hour. Clear pass/fail and free retest after repairs." },
      { title: "Full & Interim Servicing", desc: "Service book stamped, OE-quality parts. Manufacturer-warranty safe." },
      { title: "Engine Diagnostics", desc: "Professional fault-code reading and live-data testing for every major manufacturer." },
      { title: "Bodywork & Paint", desc: "Dents, scratches and bumper repairs colour-matched and finished on-site." },
    ],
    faqs: [
      { q: "How easy is it to get to you from Wapping?", a: "Quick drive via The Highway or the Limehouse Link. Sat-nav to '14 Copenhagen Place, E14 7DF' for the yard." },
      { q: "Do you handle older cars and classics?", a: "Yes — we've worked on plenty of older vehicles. Call us to discuss your specific car." },
      { q: "Will I be told before extra work happens?", a: "Always. We never carry out work without your written approval." },
      { q: "Do you offer collection from Wapping?", a: "Sometimes, depending on diary and distance — call us and we'll do our best." },
      { q: "Are you open Saturdays?", a: "Yes — Monday to Saturday 9am–6pm." },
    ],
    nearbyRoads: "The Highway, Wapping High Street, the Limehouse Link and the A1203",
    travelTime: "10 min drive from Wapping · 8 min from Tower Hill",
  },
  {
    slug: "stratford",
    name: "Stratford",
    postcode: "E15",
    hero: "Stratford Garage — MOT, Service & Repair Without the Wait",
    intro:
      "Stratford has plenty of choice — but Woodseer Autos is the independent garage East London drivers come back to. DVSA-authorised MOT, full diagnostics, premium-car experience and honest pricing. A short drive down the A11 or A12 and you're in our yard.",
    whyHere: [
      "Easy drive via the A11, A12 and Bow Roundabout",
      "Drop off and hop on the Central / Jubilee / Elizabeth line at Stratford",
      "DVSA-authorised MOT station — Classes 1, 2, 3, 4, 5 & 7",
      "Mercedes, BMW, Audi, Porsche, Range Rover all regularly serviced here",
      "MOT and service often bookable together — call to check today's availability",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT in Stratford E15", desc: "Class 4 MOTs done within the hour. Free retest after repairs done with us." },
      { title: "Servicing — All Makes", desc: "Interim, full and major services. Book stamped, OE-quality parts, manufacturer-warranty safe." },
      { title: "Diagnostics for Every Brand", desc: "Professional kit covering all major manufacturers — we diagnose, explain and quote before fixing." },
      { title: "Mechanical & Bodywork", desc: "Brakes, clutch, suspension and panel work — all done in-house with quotes upfront." },
    ],
    faqs: [
      { q: "How long does the drive from Stratford take?", a: "About 12 minutes via the A12 and Bow Roundabout. Sat-nav to '14 Copenhagen Place, E14 7DF'." },
      { q: "Can I drop off and take the Elizabeth line?", a: "Yes — drop off at the yard, take the train into work or shopping, collect later." },
      { q: "Do you work on premium cars?", a: "Yes — regular Mercedes-AMG, BMW M, Audi RS, Porsche, Range Rover and Bentley work." },
      { q: "Are walk-in MOTs available?", a: "We recommend booking, but walk-ins are welcome when the diary allows." },
      { q: "Will I get a quote before any work?", a: "Always. No work without your written approval." },
    ],
    nearbyRoads: "the A11, the A12, Bow Roundabout and Stratford High Street",
    travelTime: "12 min drive from Stratford · 10 min from Bow Roundabout",
  },
  {
    slug: "tower-hamlets",
    name: "Tower Hamlets",
    postcode: "E1 / E14",
    hero: "Tower Hamlets Garage — MOT, Servicing & Repairs",
    intro:
      "Tower Hamlets covers a huge spread — from Whitechapel and Bethnal Green in the north to Poplar, Limehouse and the Isle of Dogs in the south. Wherever you live in the borough, Woodseer Autos is a short drive away. DVSA-authorised MOT, full servicing and repairs, honest pricing — every time.",
    whyHere: [
      "Central to the borough — quick access from every Tower Hamlets postcode",
      "DVSA-authorised MOT testing station (V106376) — Classes 1, 2, 3, 4, 5 & 7",
      "Drop off and use DLR, Overground, Elizabeth line or the District/H&C",
      "All makes — daily drivers, family cars, vans and premium motors",
      "MOT and service often bookable together — call to check today's availability",
      "12-month parts and labour warranty on every repair",
    ],
    highlights: [
      { title: "MOT for Tower Hamlets Drivers", desc: "Class 4 MOTs typically completed within around an hour. Honest pass/fail with plain-English advisories. Free retest after repairs." },
      { title: "Servicing — All Makes", desc: "Interim, full and major services. Service book stamped, OE-quality parts, manufacturer warranty intact." },
      { title: "Diagnostics & Electrics", desc: "Engine management, ABS, infotainment — diagnosed properly with professional kit, explained clearly." },
      { title: "Mechanical & Bodywork", desc: "Brakes, clutch, suspension and panel work — all in-house with written quotes upfront." },
    ],
    faqs: [
      { q: "Where in Tower Hamlets are you based?", a: "We're in Poplar (E14) — central to the borough, with quick access from every Tower Hamlets postcode. Sat-nav to '14 Copenhagen Place, E14 7DF'." },
      { q: "Can I leave the car and take public transport?", a: "Yes — Poplar, Westferry, Limehouse and Bow Church DLR are all walking distance. We'll text or call when the car is ready." },
      { q: "Do you handle vans for Tower Hamlets tradespeople?", a: "Yes — light commercial vehicles up to Class 7. Many local tradespeople trust us with their work vans." },
      { q: "Will I get a written quote first?", a: "Always. No work happens without your written approval." },
      { q: "How quickly can I get a slot?", a: "Often same-day or next-day. Call us with what you need and we'll fit you in." },
    ],
    nearbyRoads: "Commercial Road, the A11, the A12, the A13 and the Limehouse Link",
    travelTime: "Central to the borough · 5–15 min from any TH postcode",
  },
];

export const Route = createFileRoute("/areas/local/$area")({
  loader: async ({ params }) => {
    const area = localAreas.find((a) => a.slug === params.area);
    if (!area) throw notFound();
    // Pull live compliance so arrival FAQs feed the FAQPage schema for this area page too.
    const config = await getPublicConfig().catch(() => null);
    return { area, compliance: config?.compliance || null };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.area;
    if (!a) return { meta: [{ title: "Area | Woodseer Autos" }] };
    const c = loaderData?.compliance;
    const mergedFaqs = [
      ...a.faqs,
      ...((c?.arrivalFaqs || []).map((f) => ({ q: f.q, a: f.a }))),
    ];
    const localBusinessLd = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "AutoRepair"],
      name: `Woodseer Autos — ${a.name}`,
      description: a.intro,
      telephone: NAP.phoneHref.replace("tel:", ""),
      url: `https://woodseerautos.co.uk/areas/local/${a.slug}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: c?.streetAddress || "16 Pixley Street",
        addressLocality: c?.addressLocality || "London",
        postalCode: c?.postalCode || "E14 7DF",
        addressCountry: c?.addressCountry || "GB",
      },
      geo: { "@type": "GeoCoordinates", latitude: 51.514512, longitude: -0.029928 },
      areaServed: { "@type": "Place", name: `${a.name}, London ${a.postcode}` },
      openingHoursSpecification: (c?.openingHours || [{ days: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], opens: "09:00", closes: "18:00" }]).map((h: { days: string[]; opens: string; closes: string }) => ({
        "@type": "OpeningHoursSpecification", dayOfWeek: h.days, opens: h.opens, closes: h.closes,
      })),
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.4", reviewCount: "116" },
      priceRange: "££",
    };
    return seoHead({
      path: `/areas/local/${params.area}`,
      title: `Garage in ${a.name} ${a.postcode} — MOT & Servicing | Woodseer Autos`,
      description: `Trusted garage in ${a.name}. DVSA-authorised MOT, servicing, diagnostics and repairs. Same-day turnaround. Call ${NAP.phone}.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Areas", path: "/areas" },
        { name: a.name, path: `/areas/local/${params.area}` },
      ],
      jsonLd: [localBusinessLd as Record<string, unknown>, faqSchema(mergedFaqs) as Record<string, unknown>],
    });
  },
  component: LocalAreaPage,
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">Area not found</h1>
        <p className="mt-2 text-muted-foreground">We couldn't find that area page.</p>
        <Link to="/areas" className="mt-4 inline-block text-primary hover:text-primary/80">View all areas →</Link>
      </div>
    </div>
  ),
});

function LocalAreaPage() {
  const { area } = Route.useLoaderData();
  const biz = getBusinessConfig();

  useEffect(() => {
    trackEvent("area_page_view", { service: area.slug });
  }, [area.slug]);

  return (
    <>
      {/* HERO with booking form */}
      <section className="relative py-12 md:py-20 overflow-hidden bg-secondary">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-1.5 text-primary text-xs font-bold uppercase tracking-[0.18em]">
                <MapPin className="h-3.5 w-3.5" />
                {area.name}, London {area.postcode}
              </div>
              <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight leading-[1.05]">
                {area.hero}
              </h1>
              <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                {area.intro}
              </p>
              <PrimaryCTA location={`area_local_${area.slug}_hero`} intent={`MOT or quote in ${area.name}`} variant="light" />
              <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {area.travelTime}</span>
              </div>
            </div>

            {/* Booking form */}
            <MOTBookingForm trackingLocation={`area_${area.slug}`} area={area.name} />
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      {/* Why drivers in this area choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary">Why {area.name} drivers choose us</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">A local garage that puts honesty first</h2>
            </div>
            <StaggerContainer className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
              {area.whyHere.map((reason: string) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                    <CheckCircle className="h-4.5 w-4.5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground leading-relaxed">{reason}</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
            <p className="mt-6 text-center text-xs text-muted-foreground">
              Easy to reach from {area.nearbyRoads}.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* Local trust signals — bespoke, area-specific proof points */}
      {area.localTrust && area.localTrust.length > 0 && (
        <AnimatedSection>
          <section className="py-14 md:py-16 border-t border-border">
            <div className="mx-auto max-w-5xl px-4 md:px-6">
              <div className="text-center max-w-2xl mx-auto">
                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Local trust signals</p>
                <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                  Why {area.name} drivers actually book us
                </h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  Verifiable details — not stock claims. Cross-check anything below before you decide.
                </p>
              </div>
              <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
                {area.localTrust.map((t: { label: string; detail: string }) => (
                  <div key={t.label} className="rounded-xl border border-border bg-card p-4">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-primary">{t.label}</dt>
                    <dd className="mt-1.5 text-sm text-foreground leading-relaxed">{t.detail}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        </AnimatedSection>
      )}

      <AnimatedSection>
        <section className="py-16 md:py-20 bg-secondary">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Services in {area.name}</h2>
              <p className="mt-2 text-muted-foreground">Everything your vehicle needs — MOTs, servicing, diagnostics and repairs, all under one roof.</p>
            </div>
            <StaggerContainer className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {area.highlights.map((h: { title: string; desc: string }) => (
                <StaggerItem key={h.title}>
                  <div className="rounded-xl border border-border bg-card p-6 hover:border-primary/40 transition-all h-full">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Wrench className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-foreground">{h.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
            <div className="mt-8">
              <p className="text-center text-[11px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-3">Jump to a service</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={"/" + s.slug as "/mot"}
                    className="rounded-lg border border-border bg-card px-4 py-3 text-center text-sm font-semibold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs">
                <Link to="/book" search={{ service: "mot", area: area.name }} className="rounded-md bg-primary/10 text-primary px-3 py-1.5 font-bold hover:bg-primary/20 transition-colors">Book MOT in {area.name}</Link>
                <Link to="/contact" className="rounded-md border border-border px-3 py-1.5 font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors">Contact the workshop</Link>
                <Link to="/reviews" className="rounded-md border border-border px-3 py-1.5 font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors">Read reviews</Link>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Helpful next steps — internal links from this local page to the high-intent
          conversion-adjacent pages. Anchor text is contextual to the area. */}
      <AnimatedSection>
        <section className="py-12 md:py-14 bg-card border-t border-border">
          <div className="mx-auto max-w-5xl px-4 md:px-6">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary text-center">Helpful next steps</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground tracking-tight text-center">Plan your visit from {area.name}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/pricing"
                onClick={() => trackEvent("service_cta_click", { location: `area_${area.slug}_internal_links`, source: "internal_link", service: "pricing", area: area.name })}
                className="group rounded-xl border border-border bg-background p-5 hover:border-primary/40 transition-colors"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Pricing</p>
                <p className="mt-2 text-base font-bold text-foreground group-hover:text-primary transition-colors">See {area.name} MOT &amp; service prices →</p>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">MOT from £35, interim service from £99 — no booking fees, fixed before work starts.</p>
              </Link>
              <Link
                to="/mot-checklist"
                onClick={() => trackEvent("service_cta_click", { location: `area_${area.slug}_internal_links`, source: "internal_link", service: "mot-checklist", area: area.name })}
                className="group rounded-xl border border-border bg-background p-5 hover:border-primary/40 transition-colors"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Pass first time</p>
                <p className="mt-2 text-base font-bold text-foreground group-hover:text-primary transition-colors">Free 30-point MOT checklist for {area.name} drivers →</p>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">15 minutes of pre-checks at home that stop the most common MOT failures.</p>
              </Link>
              <Link
                to="/garage-near-me"
                onClick={() => trackEvent("service_cta_click", { location: `area_${area.slug}_internal_links`, source: "internal_link", service: "garage-near-me", area: area.name })}
                className="group rounded-xl border border-border bg-background p-5 hover:border-primary/40 transition-colors"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Local garage</p>
                <p className="mt-2 text-base font-bold text-foreground group-hover:text-primary transition-colors">Compare garages near {area.name} →</p>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">How we compare on travel time, opening hours and DVSA accreditation across East London.</p>
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <TrustSection />

      {/* Mid-page Book MOT CTA — separately tracked from the hero so we can attribute
          conversions to people who scrolled past the highlights before clicking. */}
      <AnimatedSection>
        <section className="py-10 md:py-12 bg-primary/[0.04] border-y border-primary/15">
          <div className="mx-auto max-w-5xl px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Ready to book?</p>
              <h3 className="mt-1.5 text-xl md:text-2xl font-bold text-foreground tracking-tight">
                Book your MOT in {area.name} — we'll call to confirm.
              </h3>
            </div>
            <Link
              to="/book"
              search={{ service: "mot", area: area.name }}
              onClick={() =>
                trackEvent("service_cta_click", {
                  location: `area_${area.slug}_midpage`,
                  source: "area_midpage",
                  service: "mot",
                  area: area.name,
                })
              }
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 shrink-0"
            >
              Book MOT in {area.name} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </AnimatedSection>

      <AreaContactCTA areaName={area.name} postcode={area.postcode} source={`area_${area.slug}_cta`} />

      {/* Lightweight directions widget — pre-loaded with the verified yard entrance */}
      <AnimatedSection>
        <section className="py-14 md:py-20 bg-secondary/40 border-t border-border">
          <div className="mx-auto max-w-6xl px-4 md:px-6 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center">
            <div>
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Directions from {area.name}</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                Route straight to the yard entrance.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">
                Type your postcode (or tap "use my location") and we'll open Google Maps with the verified yard entrance pre-loaded — no wrong turns at the Pixley Street side.
              </p>
            </div>
            <DirectionsWidget source={`area_${area.slug}`} title={`Plan a route from ${area.name}`} />
          </div>
        </section>
      </AnimatedSection>

      <ArrivalFAQ
        title={`Parking & arrival when visiting from ${area.name}`}
        subtitle={`The workshop is ${area.travelTime.toLowerCase()}. Here's exactly what to expect when you pull in.`}
      />

      {/* Local FAQs */}
      <AnimatedSection>
        <FAQAccordion faqs={area.faqs} title={`FAQs from ${area.name} drivers`} />
      </AnimatedSection>

      {/* Cross-link other areas */}
      <AnimatedSection>
        <section className="py-12 bg-card border-t border-border">
          <div className="mx-auto max-w-5xl px-4 text-center">
            <h2 className="text-xl font-bold text-foreground">We also serve nearby</h2>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {localAreas.filter((a) => a.slug !== area.slug).map((a) => (
                <Link
                  key={a.slug}
                  to="/areas/local/$area"
                  params={{ area: a.slug }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" /> {a.name}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <CTABlock
        headline={`Need a Garage in ${area.name}?`}
        subtext="Honest mechanics, fair prices and quick turnaround where parts and workshop load allow. Call us or send a quick booking request."
      />
    </>
  );
}
